import { isTauri } from '../utils/tauri';
import { apibooKey, onStorageNamespaceChange } from '../services/storageNamespace';

/**
 * Per-extension persistent key-value storage, backed by a JSON file in the
 * extension's own app-data directory (`extensions/<id>/storage.json`, next to
 * its cached bundle — uninstalling the extension removes both together).
 * Backs hostBridge's `storage.get`/`storage.set` RPC methods, and is also
 * read directly host-side where a feature needs an extension's saved state
 * without the guest iframe running (e.g. DbManagerResultsTab's reconnect
 * screen needs DB Manager's saved connection config at tab-restore time).
 *
 * This is the EXTENSION'S DATA, and in practice it is credentials: DB
 * connection strings, API keys, tokens an extension was handed by the user.
 * Rust already keys the on-disk file and the keychain entries
 * (`<accountId>:<extId>:<key>`) by account; the browser fallback below is
 * namespaced to match — `apiboo-<accountId>-ext-storage:<extId>:<key>` —
 * so the two halves agree on who owns a value instead of disagreeing.
 *
 * Outside Tauri (tests, web preview) that namespaced localStorage key IS the
 * backend. There is no migration from the pre-namespace
 * `apiboo-ext-storage:<extId>:<key>` keys: an un-namespaced credential has no
 * owner, so seeding it into whichever account happens to launch first is the
 * exact leak this is fixing. purgeLegacyStorageKeys() deletes them once.
 */

const stores = new Map(); // extensionId -> Promise<object> (whole parsed file)
const writeChains = new Map(); // extensionId -> Promise (serializes file writes)

/** Rebuilt per call — the namespace moves when the signed-in account does. */
function storageKey(extensionId, key) {
  return apibooKey(`ext-storage:${extensionId}:${key}`);
}

async function persist(extensionId, store) {
  const { invoke } = await import('@tauri-apps/api/core');
  await invoke('extension_storage_write', { slug: extensionId, data: JSON.stringify(store) });
}

// Each write is the whole file, so two rapid `storage.set` calls must not
// race on disk — chain them per extension so the last stringify always lands
// last.
function enqueueWrite(extensionId, store) {
  const prev = writeChains.get(extensionId) || Promise.resolve();
  const next = prev.catch(() => {}).then(() => persist(extensionId, store));
  writeChains.set(extensionId, next);
  return next;
}

async function loadStore(extensionId) {
  const { invoke } = await import('@tauri-apps/api/core');
  const raw = await invoke('extension_storage_read', { slug: extensionId });
  if (raw != null) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      // corrupted file — start fresh rather than wedging every storage call
    }
  }
  // No file for THIS account yet — an empty store, never seeded from another
  // account's leftovers.
  return {};
}

/**
 * Drop the in-memory per-extension caches. The parsed `storage.json` for
 * account A must not answer a read made by account B — the file path Rust
 * builds already moved, but this cache would have outlived the switch.
 */
export function resetExtensionStorageCaches() {
  stores.clear();
  writeChains.clear();
}

function getStore(extensionId) {
  let promise = stores.get(extensionId);
  if (!promise) {
    promise = loadStore(extensionId).catch((err) => {
      stores.delete(extensionId); // failed load must not be cached — allow retry
      throw err;
    });
    stores.set(extensionId, promise);
  }
  return promise;
}

export async function getExtensionStorageValue(extensionId, key) {
  if (!isTauri()) {
    const raw = localStorage.getItem(storageKey(extensionId, key));
    return raw != null ? JSON.parse(raw) : null;
  }
  const store = await getStore(extensionId);
  return Object.prototype.hasOwnProperty.call(store, key) ? store[key] : null;
}

export async function setExtensionStorageValue(extensionId, key, value) {
  if (!isTauri()) {
    localStorage.setItem(storageKey(extensionId, key), JSON.stringify(value));
    return;
  }
  const store = await getStore(extensionId);
  store[key] = value;
  await enqueueWrite(extensionId, store);
}

// An account switch invalidates every cached `storage.json`.
onStorageNamespaceChange(resetExtensionStorageCaches);
